import React, { useEffect, useState } from 'react';
import { 
  UserProfile, PromoCode, PromoRedemption, AdminAction,
  EventLog, UserWithStatus, UserTimeline, IPAnalytics, DomainAnalytics,
  UserRiskStatus, ControlStatus, DomainControlStatus,
  Generation, CreditLedgerEntry, GenerationStatus, SalesMetrics, SalesPeriod
} from '../types';
import IPWorldMap from './IPWorldMap';
import UserBillingCard from './admin/UserBillingCard';
import InvoicingTab from './admin/InvoicingTab';
import {
  Shield, Search, Zap, Users, Gift, Activity, X, Check, 
  AlertCircle, Loader2, Plus, Edit3, XCircle, CheckCircle,
  TrendingUp, DollarSign, Award, Calendar, Clock,
  Globe, Wifi, Eye, Ban, AlertTriangle, ChevronRight,
  RefreshCw, Filter, User, ArrowLeft, Image, CreditCard,
  ExternalLink, Coins, Trash2, BarChart3, Percent, Target,
  Download, ClipboardList, Upload, Send, Play, MapPin, Receipt
} from 'lucide-react';
import { openHubAdminUser } from '@rehub/admin';
import { formatProviderCostUsd, formatRecordedRevenue, recordedRevenueCaption } from '@rehub/pricing';
import { AdminShell } from '@rehub/admin/ui';
import {
  searchUsers,
  grantBonusCredits,
  getPromoCodes,
  getPromoRedemptions,
  createPromoCode,
  updatePromoCode,
  getAdminActions,
  getActivityFeed,
  getUsersWithStatus,
  getUserTimeline,
  updateUserStatus,
  getIPAnalytics,
  updateIPControl,
  getDomainAnalytics,
  updateDomainControl,
  getGenerations,
  getUserGenerations,
  getUserCreditLedger,
  adjustUserCredits,
  getImageUrl,
  getThumbnailUrl,
  cleanupStuckGenerations,
  getStuckGenerationsCount,
  getSalesMetrics,
  getEditors,
  getDefaultEditors,
  addEditor,
  addEditorByEmail,
  removeEditor,
  setDefaultEditors,
  createEditorUser,
  sendEditorLoginLink,
  supabase,
  type EditorRow,
  type DefaultEditorRow
} from '../services/supabase';

interface AdminPanelProps {
  user: UserProfile;
  adminRole: 'admin' | 'superadmin' | 'editor';
  onClose: () => void;
}

type AdminTab = 'orders' | 'activity' | 'users' | 'ips' | 'domains' | 'promos' | 'audit' | 'generations' | 'sales' | 'editors' | 'invoicing';

// Pro Order types
interface ProOrder {
  id: string;
  job_number: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
  property_address: string;
  estimated_sq_ft: number | null;
  property_type: string;
  floors: number;
  turnaround: string;
  notes: string | null;
  sketch_url: string;
  contact_email: string;
  contact_phone: string | null;
  quoted_price: number | null;
  final_price: number | null;
  // Mirrors the pro_orders DB CHECK constraint (incl. card-payment flow).
  status: 'awaiting_payment' | 'pending_payment' | 'pending' | 'quoted' | 'paid' | 'in_progress' | 'completed' | 'cancelled';
  output_url: string | null;
  delivered_at: string | null;
  admin_notes: string | null;
  assigned_editor_id: string | null;
  started_at: string | null;
  due_at: string | null;
}

type OrderStatus = ProOrder['status'];

/** sketch_url can be a single URL string or a JSON array of URLs (e.g. from pro orders). Returns all URLs. */
function getAllSketchUrls(sketchUrl: string | null | undefined): string[] {
  if (!sketchUrl?.trim()) return [];
  const trimmed = sketchUrl.trim();
  if (trimmed.startsWith('[')) {
    try {
      const arr = JSON.parse(trimmed) as unknown;
      if (Array.isArray(arr)) return arr.filter((u): u is string => typeof u === 'string');
    } catch {
      // fallback: use as-is
    }
  }
  return [trimmed];
}

function getFirstSketchUrl(sketchUrl: string | null | undefined): string | null {
  return getAllSketchUrls(sketchUrl)[0] ?? null;
}

type Badge = { bg: string; text: string; label: string };

const ORDER_STATUS_COLORS: Record<OrderStatus, Badge> = {
  awaiting_payment: { bg: 'bg-slate-800/60 border-slate-700', text: 'text-slate-400', label: 'Awaiting Payment' },
  pending_payment: { bg: 'bg-slate-800/60 border-slate-700', text: 'text-slate-400', label: 'Awaiting Payment' },
  pending: { bg: 'bg-amber-900/30 border-amber-800', text: 'text-amber-400', label: 'Pending' },
  quoted: { bg: 'bg-purple-900/30 border-purple-800', text: 'text-purple-400', label: 'Quoted' },
  paid: { bg: 'bg-teal-900/30 border-teal-800', text: 'text-teal-400', label: 'Paid' },
  in_progress: { bg: 'bg-blue-900/30 border-blue-800', text: 'text-blue-400', label: 'In Progress' },
  completed: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'Completed' },
  cancelled: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Cancelled' }
};

// Never crash on a status/key that isn't in a colour map (e.g. a new DB enum value).
// Falls back to a neutral badge with a humanised label.
function badgeStyle<T extends string>(map: Record<T, Badge>, key: T | string | null | undefined): Badge {
  const found = key != null ? map[key as T] : undefined;
  if (found) return found;
  const label = key ? String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';
  return { bg: 'bg-slate-700 border-slate-600', text: 'text-slate-300', label };
}

const STATUS_COLORS: Record<UserRiskStatus, { bg: string; text: string; label: string }> = {
  ok: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'OK' },
  watch: { bg: 'bg-yellow-900/30 border-yellow-800', text: 'text-yellow-400', label: 'Watch' },
  restricted: { bg: 'bg-teal-900/30 border-teal-800', text: 'text-teal-400', label: 'Restricted' },
  banned: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Banned' }
};

const CONTROL_COLORS: Record<ControlStatus | DomainControlStatus, { bg: string; text: string; label: string }> = {
  ok: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'OK' },
  throttled: { bg: 'bg-yellow-900/30 border-yellow-800', text: 'text-yellow-400', label: 'Throttled' },
  require_payment: { bg: 'bg-teal-900/30 border-teal-800', text: 'text-teal-400', label: 'Require Payment' },
  blocked: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Blocked' }
};

const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  magic_link_requested: { label: 'Magic Link', color: 'bg-blue-900/30 text-blue-400' },
  login: { label: 'Login', color: 'bg-green-900/30 text-green-400' },
  generation_started: { label: 'Gen Started', color: 'bg-purple-900/30 text-purple-400' },
  generation_completed: { label: 'Gen Complete', color: 'bg-emerald-900/30 text-emerald-400' },
  generation_failed: { label: 'Gen Failed', color: 'bg-red-900/30 text-red-400' },
  credits_granted: { label: 'Credits+', color: 'bg-teal-900/30 text-teal-400' },
  credits_used: { label: 'Credits-', color: 'bg-slate-700 text-slate-400' },
  payment_completed: { label: 'Payment', color: 'bg-green-900/30 text-green-400' },
  promo_redeemed: { label: 'Promo', color: 'bg-pink-900/30 text-pink-400' },
  user_status_changed: { label: 'Status Δ', color: 'bg-yellow-900/30 text-yellow-400' },
  ip_control_changed: { label: 'IP Ctrl', color: 'bg-cyan-900/30 text-cyan-400' },
  domain_control_changed: { label: 'Domain Ctrl', color: 'bg-indigo-900/30 text-indigo-400' }
};

const GENERATION_STATUS_COLORS: Record<GenerationStatus, { bg: string; text: string; label: string }> = {
  queued: { bg: 'bg-slate-700', text: 'text-slate-400', label: 'Queued' },
  started: { bg: 'bg-blue-900/30 border-blue-800', text: 'text-blue-400', label: 'Processing' },
  completed: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'Completed' },
  failed: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Failed' }
};

export default function AdminPanel({ user, adminRole, onClose }: AdminPanelProps) {
  const isSuperAdmin = adminRole === 'superadmin';
  const [activeTab, setActiveTab] = useState<AdminTab>('orders');

  // Pro Orders State
  const [orders, setOrders] = useState<ProOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ProOrder | null>(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus | ''>('');
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [uploadingOutput, setUploadingOutput] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [sketchLoadError, setSketchLoadError] = useState(false);
  const [sketchDownloadUrl, setSketchDownloadUrl] = useState<string | null>(null);
  const [allSketchDownloadUrls, setAllSketchDownloadUrls] = useState<string[]>([]);
  const [selectedSketchIdx, setSelectedSketchIdx] = useState(0);
  
  // User Search & Grant Credits
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantMessage, setGrantMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Promo Codes
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoCredits, setNewPromoCredits] = useState('');
  const [newPromoMaxUses, setNewPromoMaxUses] = useState('');
  const [newPromoExpires, setNewPromoExpires] = useState('');
  const [newPromoDescription, setNewPromoDescription] = useState('');
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [editPromoCredits, setEditPromoCredits] = useState('');
  const [editPromoMaxUses, setEditPromoMaxUses] = useState('');
  const [editPromoExpires, setEditPromoExpires] = useState('');
  const [editPromoDescription, setEditPromoDescription] = useState('');
  const [editPromoActive, setEditPromoActive] = useState(true);
  const [updatingPromo, setUpdatingPromo] = useState(false);

  // Audit Log
  const [auditLog, setAuditLog] = useState<AdminAction[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Promo Redemptions
  const [redemptions, setRedemptions] = useState<PromoRedemption[]>([]);

  // Activity Feed
  const [activityEvents, setActivityEvents] = useState<EventLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [activityAppFilter, setActivityAppFilter] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<EventLog | null>(null);

  // Enhanced Users
  const [usersWithStatus, setUsersWithStatus] = useState<UserWithStatus[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userStatusFilter, setUserStatusFilter] = useState<UserRiskStatus | ''>('');
  const [selectedUserDetail, setSelectedUserDetail] = useState<UserTimeline | null>(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [statusChangeUser, setStatusChangeUser] = useState<UserWithStatus | null>(null);
  const [newStatus, setNewStatus] = useState<UserRiskStatus>('ok');
  const [statusReason, setStatusReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // IP Analytics
  const [ipAnalytics, setIpAnalytics] = useState<IPAnalytics[]>([]);
  const [loadingIPs, setLoadingIPs] = useState(false);
  const [ipError, setIpError] = useState<string | null>(null);
  const [ipDays, setIpDays] = useState(30);
  const [selectedIP, setSelectedIP] = useState<IPAnalytics | null>(null);
  const [ipControlStatus, setIpControlStatus] = useState<ControlStatus>('ok');
  const [ipControlReason, setIpControlReason] = useState('');
  const [ipThrottle, setIpThrottle] = useState('1.0');
  const [updatingIP, setUpdatingIP] = useState(false);

  // Domain Analytics
  const [domainAnalytics, setDomainAnalytics] = useState<DomainAnalytics[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainAnalytics | null>(null);
  const [domainControlStatus, setDomainControlStatus] = useState<DomainControlStatus>('ok');
  const [domainControlReason, setDomainControlReason] = useState('');
  const [domainThrottle, setDomainThrottle] = useState('1.0');
  const [updatingDomain, setUpdatingDomain] = useState(false);

  // Generations
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loadingGenerations, setLoadingGenerations] = useState(false);
  const [generationsTotal, setGenerationsTotal] = useState(0);
  const [generationsPage, setGenerationsPage] = useState(0);
  const [generationStatusFilter, setGenerationStatusFilter] = useState<GenerationStatus | ''>('');
  const [generationEmailFilter, setGenerationEmailFilter] = useState('');
  const [generationDateFrom, setGenerationDateFrom] = useState('');
  const [generationDateTo, setGenerationDateTo] = useState('');

  // User Detail - Generations & Credit Ledger
  const [userGenerations, setUserGenerations] = useState<Generation[]>([]);
  const [loadingUserGenerations, setLoadingUserGenerations] = useState(false);
  const [userCreditLedger, setUserCreditLedger] = useState<CreditLedgerEntry[]>([]);
  const [userCreditBalance, setUserCreditBalance] = useState<number>(0);
  const [loadingCreditLedger, setLoadingCreditLedger] = useState(false);
  const [adjustCreditsAmount, setAdjustCreditsAmount] = useState('');
  const [adjustCreditsReason, setAdjustCreditsReason] = useState('');
  const [adjustingCredits, setAdjustingCredits] = useState(false);
  const [showAdjustCredits, setShowAdjustCredits] = useState(false);
  const [userDetailTab, setUserDetailTab] = useState<'timeline' | 'generations' | 'credits'>('timeline');

  // Stuck generations cleanup
  const [stuckCount, setStuckCount] = useState(0);
  const [cleaningStuck, setCleaningStuck] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ cleaned: number; refunded: number } | null>(null);

  // Sales Metrics
  const [salesMetrics, setSalesMetrics] = useState<SalesMetrics | null>(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesPeriod, setSalesPeriod] = useState<SalesPeriod>('30d');

  // Editors (superadmin)
  const [editors, setEditors] = useState<EditorRow[]>([]);
  const [defaultEditors, setDefaultEditors] = useState<DefaultEditorRow[]>([]);
  const [loadingEditors, setLoadingEditors] = useState(false);
  const [newEditorUserId, setNewEditorUserId] = useState('');
  const [addingEditor, setAddingEditor] = useState(false);
  const [newEditorEmail, setNewEditorEmail] = useState('');
  const [newEditorPassword, setNewEditorPassword] = useState('');
  const [creatingEditor, setCreatingEditor] = useState(false);
  const [sendingEditorLinkFor, setSendingEditorLinkFor] = useState<string | null>(null);

  // Image download
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Handle download
  const handleDownload = async (imageUrl: string | null, id: string) => {
    if (!imageUrl) return;
    setDownloadingId(id);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generation-${id.substring(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloadingId(null);
  };

  // Load data based on active tab
  useEffect(() => {
    if (!isSuperAdmin && activeTab !== 'orders') {
      setActiveTab('orders');
      return;
    }

    if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'activity') {
      loadActivityFeed();
    } else if (activeTab === 'users') {
      loadUsersWithStatus();
    } else if (activeTab === 'ips') {
      loadIPAnalytics();
    } else if (activeTab === 'domains') {
      loadDomainAnalytics();
    } else if (activeTab === 'promos') {
      loadPromoCodes();
      loadRedemptions();
    } else if (activeTab === 'audit') {
      loadAuditLog();
    } else if (activeTab === 'generations') {
      loadGenerations();
    } else if (activeTab === 'sales') {
      loadSalesMetrics();
    } else if (activeTab === 'editors') {
      loadEditorsData();
    }
  }, [activeTab, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setActiveTab('orders');
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    setSketchLoadError(false);
    setSelectedSketchIdx(0);
  }, [selectedOrder?.id, selectedOrder?.sketch_url]);

  useEffect(() => {
    const resolveAllSketchUrls = async () => {
      const raw = selectedOrder?.sketch_url;
      if (!raw) {
        setSketchDownloadUrl(null);
        setAllSketchDownloadUrls([]);
        return;
      }

      const urls = getAllSketchUrls(raw);
      if (urls.length === 0) {
        setSketchDownloadUrl(null);
        setAllSketchDownloadUrls([]);
        return;
      }

      const resolvedUrls: string[] = [];

      for (const url of urls) {
        if (url.startsWith('blob:')) {
          resolvedUrls.push(url);
          continue;
        }

        const splitToken = '/images/';
        const storagePath = url.includes(splitToken)
          ? url.split(splitToken)[1]?.split('?')[0] || null
          : null;

        if (!storagePath) {
          resolvedUrls.push(url);
          continue;
        }

        try {
          const { supabase } = await import('../services/supabase');

          const { data: signedData, error: signedError } = await supabase.functions.invoke('get-sketch-url', {
            body: { sketch_url: url },
          });

          if (!signedError && signedData?.signed_url) {
            resolvedUrls.push(signedData.signed_url);
            continue;
          }

          const { data, error } = await supabase.storage
            .from('images')
            .createSignedUrl(storagePath, 60 * 60);
          if (!error && data?.signedUrl) {
            resolvedUrls.push(data.signedUrl);
            continue;
          }

          resolvedUrls.push(url);
        } catch (err) {
          console.error('Failed to create signed sketch URL:', err);
          resolvedUrls.push(url);
        }
      }

      setAllSketchDownloadUrls(resolvedUrls);
      setSketchDownloadUrl(resolvedUrls[0] || null);
    };

    resolveAllSketchUrls();
  }, [selectedOrder?.id, selectedOrder?.sketch_url]);

  // ==================== ORDERS FUNCTIONS ====================
  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const { supabase } = await import('../services/supabase');
      let query = supabase
        .from('pro_orders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (orderStatusFilter) {
        query = query.eq('status', orderStatusFilter);
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) {
        console.error('Error loading orders:', error);
      } else {
        setOrders(data || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
    setLoadingOrders(false);
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus) => {
    setUpdatingOrder(true);
    try {
      const { supabase } = await import('../services/supabase');
      const { data, error } = await supabase.rpc('pro_orders_update_status', {
        p_order_id: orderId,
        p_status: status
      });
      if (error) {
        console.error('Error updating order:', error);
        alert(`Failed to update order: ${error.message}`);
        return;
      }
      const updated = (typeof data === 'string' ? JSON.parse(data) : data) as ProOrder | null;
      if (updated) {
        loadOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({ ...selectedOrder, ...updated });
        }
      } else {
        alert('Failed to update order. You may not have permission.');
      }
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order. Please try again.');
    } finally {
      setUpdatingOrder(false);
    }
  };

  const uploadOutputFiles = async (orderId: string, files: File[]) => {
    setUploadingOutput(true);
    try {
      const { supabase, uploadImageToStorage } = await import('../services/supabase');

      const existingUrls = getAllSketchUrls(selectedOrder?.output_url);

      const newUrls: string[] = [];
      for (const file of files) {
        const url = await uploadImageToStorage(orderId, file, 'output', crypto.randomUUID());
        if (url) newUrls.push(url);
      }

      if (newUrls.length > 0) {
        const allUrls = [...existingUrls, ...newUrls];
        const outputValue = allUrls.length === 1 ? allUrls[0] : JSON.stringify(allUrls);

        const { error } = await supabase
          .from('pro_orders')
          .update({
            output_url: outputValue,
            status: 'completed',
            delivered_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', orderId);

        if (!error) {
          loadOrders();
          if (selectedOrder?.id === orderId) {
            setSelectedOrder({ ...selectedOrder, output_url: outputValue, status: 'completed' });
          }
        }
      }
    } catch (err) {
      console.error('Error uploading output:', err);
    }
    setUploadingOutput(false);
  };

  const removeOutputFile = async (orderId: string, urlToRemove: string) => {
    try {
      const { supabase } = await import('../services/supabase');
      const existingUrls = getAllSketchUrls(selectedOrder?.output_url);
      const remaining = existingUrls.filter(u => u !== urlToRemove);
      const outputValue = remaining.length === 0 ? null
        : remaining.length === 1 ? remaining[0]
        : JSON.stringify(remaining);
      const status = remaining.length === 0 ? 'in_progress' as const : 'completed' as const;

      const { error } = await supabase
        .from('pro_orders')
        .update({
          output_url: outputValue,
          status,
          ...(remaining.length === 0 ? { delivered_at: null } : {}),
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (!error) {
        loadOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({ ...selectedOrder, output_url: outputValue, status });
        }
      }
    } catch (err) {
      console.error('Error removing output file:', err);
    }
  };

  const sendDeliveryEmail = async (order: ProOrder) => {
    const outputUrls = getAllSketchUrls(order.output_url);
    if (outputUrls.length === 0) {
      alert('Please upload the completed floor plan first.');
      return;
    }
    
    try {
      const { supabase } = await import('../services/supabase');
      
      // Call edge function to send delivery email
      await supabase.functions.invoke('notify-delivery', {
        body: {
          order_id: order.id,
          contact_email: order.contact_email,
          property_address: order.property_address,
          output_url: outputUrls[0],
          output_urls: outputUrls,
        },
      });
      
      alert('Delivery email sent to ' + order.contact_email);
    } catch (err) {
      console.error('Error sending delivery email:', err);
      alert('Failed to send email. Please send manually.');
    }
  };

  const deleteOrder = async (orderId: string) => {
    if (!isSuperAdmin) {
      alert('Only superadmins can delete orders');
      return;
    }
    const confirmed = window.confirm('Delete this order permanently? This cannot be undone.');
    if (!confirmed) return;

    setDeletingOrderId(orderId);
    try {
      const { supabase } = await import('../services/supabase');
      console.log('Attempting to delete order:', orderId);
      
      const { data, error, count } = await supabase
        .from('pro_orders')
        .delete()
        .eq('id', orderId)
        .select();

      console.log('Delete response:', { data, error, count });

      if (error) {
        console.error('Supabase delete error:', error);
        alert(`Delete failed: ${error.message}`);
        return;
      }

      if (!data || data.length === 0) {
        console.warn('No rows deleted - check RLS policies');
        alert('Delete failed - no rows affected. Your account may not have superadmin permissions in the database.');
        return; // Don't remove from local state if delete didn't actually work
      }
      
      // Only remove from local state after confirmed deletion
      setOrders((prev) => prev.filter((order) => order.id !== orderId));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
      }
      alert(`Order ${orderId.slice(0, 8)}... deleted successfully`);
    } catch (err: any) {
      console.error('Error deleting order:', err);
      alert(`Failed to delete order: ${err?.message || 'Unknown error'}`);
    } finally {
      setDeletingOrderId(null);
    }
  };
  // ==================== END ORDERS FUNCTIONS ====================

  const loadSalesMetrics = async () => {
    setLoadingSales(true);
    let dateFrom: string | undefined;
    const now = new Date();
    
    if (salesPeriod === '7d') {
      dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (salesPeriod === '30d') {
      dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }
    // 'all' leaves dateFrom undefined
    
    const result = await getSalesMetrics(dateFrom);
    if (result.success && result.metrics) {
      setSalesMetrics(result.metrics);
    }
    setLoadingSales(false);
  };

  const loadEditorsData = async () => {
    setLoadingEditors(true);
    try {
      const [e, d] = await Promise.all([getEditors(), getDefaultEditors()]);
      setEditors(e);
      setDefaultEditors(d);
    } catch (err) {
      console.error('Load editors error:', err);
    }
    setLoadingEditors(false);
  };

  const loadPromoCodes = async () => {
    setLoadingPromos(true);
    const data = await getPromoCodes();
    setPromoCodes(data);
    setLoadingPromos(false);
  };

  const loadRedemptions = async () => {
    const data = await getPromoRedemptions();
    setRedemptions(data);
  };

  const loadAuditLog = async () => {
    setLoadingAudit(true);
    const data = await getAdminActions();
    setAuditLog(data);
    setLoadingAudit(false);
  };

  const loadActivityFeed = async () => {
    setLoadingActivity(true);
    const result = await getActivityFeed(100, 0, activityFilter || undefined);
    if (result.success && result.events) {
      setActivityEvents(result.events);
      setActivityTotal(result.total || 0);
    }
    setLoadingActivity(false);
  };

  const loadUsersWithStatus = async () => {
    setLoadingUsers(true);
    const result = await getUsersWithStatus(50, 0, searchTerm || undefined, userStatusFilter || undefined);
    if (result.success && result.users) {
      setUsersWithStatus(result.users);
      setUsersTotal(result.total || 0);
    }
    setLoadingUsers(false);
  };

  const loadUserDetail = async (userId: string) => {
    setLoadingUserDetail(true);
    const result = await getUserTimeline(userId);
    if (result.success && result.user) {
      setSelectedUserDetail({ user: result.user, events: result.events || [] });
      // Also load generations and credit ledger
      loadUserGenerationsAndLedger(userId);
    }
    setLoadingUserDetail(false);
  };

  const handleUpdateUserStatus = async () => {
    if (!statusChangeUser) return;
    setUpdatingStatus(true);
    const result = await updateUserStatus(statusChangeUser.id, newStatus, statusReason || undefined);
    if (result.success) {
      await loadUsersWithStatus();
      setStatusChangeUser(null);
      setStatusReason('');
    }
    setUpdatingStatus(false);
  };

  const loadIPAnalytics = async (days?: number) => {
    setLoadingIPs(true);
    setIpError(null);
    const result = await getIPAnalytics(50, days ?? ipDays);
    if (result.success && result.ips) {
      setIpAnalytics(result.ips);
    } else if (!result.success) {
      setIpError(result.error || 'Unknown error loading IP analytics');
      console.error('IP Analytics error:', result);
    }
    setLoadingIPs(false);
  };

  const handleUpdateIPControl = async () => {
    if (!selectedIP) return;
    setUpdatingIP(true);
    const result = await updateIPControl(
      selectedIP.ip_address,
      ipControlStatus,
      parseFloat(ipThrottle) || 1.0,
      ipControlReason || undefined
    );
    if (result.success) {
      await loadIPAnalytics();
      setSelectedIP(null);
      setIpControlReason('');
    }
    setUpdatingIP(false);
  };

  const loadDomainAnalytics = async () => {
    setLoadingDomains(true);
    const result = await getDomainAnalytics(50);
    if (result.success && result.domains) {
      setDomainAnalytics(result.domains);
    }
    setLoadingDomains(false);
  };

  const loadGenerations = async () => {
    setLoadingGenerations(true);
    const result = await getGenerations({
      limit: 12,
      offset: generationsPage * 12,
      status: generationStatusFilter || undefined,
      userEmail: generationEmailFilter || undefined,
      dateFrom: generationDateFrom || undefined,
      dateTo: generationDateTo || undefined
    });
    if (result.success && result.generations) {
      setGenerations(result.generations);
      setGenerationsTotal(result.total || 0);
    }
    setLoadingGenerations(false);
    // Also load stuck count
    loadStuckCount();
  };

  const loadStuckCount = async () => {
    const count = await getStuckGenerationsCount();
    setStuckCount(count);
  };

  const handleCleanupStuck = async () => {
    setCleaningStuck(true);
    setCleanupResult(null);
    const result = await cleanupStuckGenerations();
    if (result.success) {
      setCleanupResult({ 
        cleaned: result.generations_cleaned || 0, 
        refunded: result.credits_refunded || 0 
      });
      // Refresh generations and stuck count
      await loadGenerations();
    }
    setCleaningStuck(false);
  };

  const loadUserGenerationsAndLedger = async (userId: string) => {
    setLoadingUserGenerations(true);
    setLoadingCreditLedger(true);
    
    const [genResult, ledgerResult] = await Promise.all([
      getUserGenerations(userId, 20),
      getUserCreditLedger(userId, 50)
    ]);
    
    if (genResult.success && genResult.generations) {
      setUserGenerations(genResult.generations);
    }
    if (ledgerResult.success) {
      setUserCreditLedger(ledgerResult.entries || []);
      setUserCreditBalance(ledgerResult.balance || 0);
    }
    
    setLoadingUserGenerations(false);
    setLoadingCreditLedger(false);
  };

  const handleAdjustCredits = async () => {
    if (!selectedUserDetail || !adjustCreditsAmount || !adjustCreditsReason.trim()) return;
    
    setAdjustingCredits(true);
    const result = await adjustUserCredits(
      selectedUserDetail.user.id,
      parseInt(adjustCreditsAmount),
      adjustCreditsReason
    );
    
    if (result.success) {
      // Refresh user data
      await loadUserGenerationsAndLedger(selectedUserDetail.user.id);
      await loadUserDetail(selectedUserDetail.user.id);
      setShowAdjustCredits(false);
      setAdjustCreditsAmount('');
      setAdjustCreditsReason('');
    }
    setAdjustingCredits(false);
  };

  const handleUpdateDomainControl = async () => {
    if (!selectedDomain) return;
    setUpdatingDomain(true);
    const result = await updateDomainControl(
      selectedDomain.domain,
      domainControlStatus,
      parseFloat(domainThrottle) || 1.0,
      domainControlReason || undefined
    );
    if (result.success) {
      await loadDomainAnalytics();
      setSelectedDomain(null);
      setDomainControlReason('');
    }
    setUpdatingDomain(false);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      return;
    }

    setSearching(true);
    try {
      const results = await searchUsers(searchTerm);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleGrantCredits = async () => {
    if (!selectedUser || !creditAmount || parseInt(creditAmount) <= 0) {
      setGrantMessage({ type: 'error', text: 'Please select a user and enter a valid credit amount' });
      return;
    }

    if (!creditReason.trim()) {
      setGrantMessage({ type: 'error', text: 'Please provide a reason for this credit grant' });
      return;
    }

    setGranting(true);
    setGrantMessage(null);

    try {
      const result = await grantBonusCredits(
        selectedUser.id,
        parseInt(creditAmount),
        creditReason
      );

      if (result.success) {
        setGrantMessage({ 
          type: 'success', 
          text: `Successfully granted ${creditAmount} credits to ${selectedUser.email}` 
        });
        setCreditAmount('');
        setCreditReason('');
        setSelectedUser(null);
        setSearchResults([]);
        setSearchTerm('');
      } else {
        setGrantMessage({ type: 'error', text: result.error || 'Failed to grant credits' });
      }
    } catch (err: any) {
      setGrantMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setGranting(false);
    }
  };

  const handleCreatePromo = async () => {
    if (!newPromoCode.trim() || !newPromoCredits || parseInt(newPromoCredits) <= 0) {
      setPromoMessage({ type: 'error', text: 'Code and credits are required' });
      return;
    }

    setCreatingPromo(true);
    setPromoMessage(null);

    try {
      const result = await createPromoCode(
        newPromoCode.trim(),
        parseInt(newPromoCredits),
        newPromoMaxUses ? parseInt(newPromoMaxUses) : null,
        newPromoExpires || null,
        newPromoDescription || null
      );

      if (result.success) {
        setPromoMessage({ type: 'success', text: `Promo code "${result.promo_id}" created successfully` });
        setNewPromoCode('');
        setNewPromoCredits('');
        setNewPromoMaxUses('');
        setNewPromoExpires('');
        setNewPromoDescription('');
        setShowCreatePromo(false);
        await loadPromoCodes();
      } else {
        const errorMessages: Record<string, string> = {
          'INVALID_CODE_LENGTH': 'Code must be 3-20 characters',
          'INVALID_CODE_FORMAT': 'Code must be alphanumeric (A-Z, 0-9)',
          'CODE_EXISTS': 'This code already exists',
          'NOT_AUTHORIZED': 'You are not authorized to create promo codes'
        };
        setPromoMessage({ type: 'error', text: errorMessages[result.error || ''] || result.error || 'Failed to create code' });
      }
    } catch (err: any) {
      setPromoMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleTogglePromo = async (promoId: string, currentActive: boolean) => {
    try {
      const result = await updatePromoCode(promoId, !currentActive);
      if (result.success) {
        await loadPromoCodes();
      }
    } catch (err) {
      console.error('Toggle promo error:', err);
    }
  };

  const handleStartEditPromo = (promo: PromoCode) => {
    setEditingPromoId(promo.id);
    setEditPromoCredits(promo.credits.toString());
    setEditPromoMaxUses(promo.max_uses?.toString() || '');
    setEditPromoExpires(promo.expires_at ? promo.expires_at.split('T')[0] : '');
    setEditPromoDescription(promo.description || '');
    setEditPromoActive(promo.active);
    setPromoMessage(null);
  };

  const handleCancelEditPromo = () => {
    setEditingPromoId(null);
    setEditPromoCredits('');
    setEditPromoMaxUses('');
    setEditPromoExpires('');
    setEditPromoDescription('');
    setEditPromoActive(true);
    setPromoMessage(null);
  };

  const handleUpdatePromo = async (promoId: string) => {
    if (!editPromoCredits || parseInt(editPromoCredits) <= 0) {
      setPromoMessage({ type: 'error', text: 'Credits must be greater than 0' });
      return;
    }

    setUpdatingPromo(true);
    setPromoMessage(null);

    try {
      const result = await updatePromoCode(
        promoId,
        editPromoActive,
        parseInt(editPromoCredits),
        editPromoMaxUses ? parseInt(editPromoMaxUses) : null,
        editPromoExpires || null,
        editPromoDescription || null
      );

      if (result.success) {
        setPromoMessage({ type: 'success', text: 'Promo code updated successfully' });
        setEditingPromoId(null);
        await loadPromoCodes();
      } else {
        setPromoMessage({ type: 'error', text: result.error || 'Failed to update code' });
      }
    } catch (err: any) {
      setPromoMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setUpdatingPromo(false);
    }
  };

  const getPromoStatus = (promo: PromoCode) => {
    if (!promo.active) return { label: 'Inactive', color: 'bg-slate-700 text-slate-400' };
    if (promo.expires_at && new Date(promo.expires_at) < new Date()) {
      return { label: 'Expired', color: 'bg-red-900/30 text-red-400 border border-red-800' };
    }
    if (promo.max_uses && promo.current_uses >= promo.max_uses) {
      return { label: 'Exhausted', color: 'bg-yellow-900/30 text-yellow-400 border border-yellow-800' };
    }
    return { label: 'Active', color: 'bg-green-900/30 text-green-400 border border-green-800' };
  };

  const pendingOrders = orders.filter((o) => o.status === 'pending').length;
  const adminTabs: { id: AdminTab; label: string; icon: React.ElementType; badge?: number }[] = [
    { id: 'orders', label: 'Floor plans', icon: ClipboardList, badge: pendingOrders > 0 ? pendingOrders : undefined },
  ];
  if (isSuperAdmin) {
    adminTabs.unshift(
      { id: 'activity', label: 'Activity', icon: Activity },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'ips', label: 'IPs', icon: Wifi },
      { id: 'domains', label: 'Domains', icon: Globe },
      { id: 'promos', label: 'Promos', icon: Gift },
      { id: 'sales', label: 'Sales', icon: BarChart3 },
      { id: 'editors', label: 'Editors', icon: Users },
      { id: 'generations', label: 'Generations', icon: Image },
    );
    adminTabs.push(
      { id: 'invoicing', label: 'Invoicing', icon: Receipt },
      { id: 'audit', label: 'Audit', icon: Shield },
    );
  }

  const adminPageCopy: Record<AdminTab, { title: string; description: string }> = {
    orders: { title: 'Floor plan orders', description: 'Process and deliver floor plans' },
    activity: { title: 'Activity', description: 'Real-time event log' },
    users: { title: 'Users', description: 'Sketchplan activity. Credits, status and free HDR edits are in Hub.' },
    ips: { title: 'IP addresses', description: 'Monitor and control IP addresses' },
    domains: { title: 'Domains', description: 'Monitor and control email domains' },
    promos: { title: 'Promo codes', description: 'Create and manage promotional codes' },
    audit: { title: 'Audit', description: 'View admin activity history' },
    editors: { title: 'Editors', description: 'Assign editors and set who receives new orders' },
    generations: { title: 'Generations', description: 'View image generations across users' },
    sales: { title: 'Sales and costs', description: 'Profitability metrics and cost analysis' },
    invoicing: { title: 'Invoicing', description: 'Monthly-billed clients, statements and exports' },
  };

  const refreshAdmin = () => {
    if (activeTab === 'activity') loadActivityFeed();
    else if (activeTab === 'users') loadUsersWithStatus();
    else if (activeTab === 'ips') loadIPAnalytics();
    else if (activeTab === 'domains') loadDomainAnalytics();
    else if (activeTab === 'generations') loadGenerations();
    else if (activeTab === 'sales') loadSalesMetrics();
    else if (activeTab === 'editors') loadEditorsData();
  };

  const canRefresh = isSuperAdmin && (activeTab === 'activity' || activeTab === 'users' || activeTab === 'ips' || activeTab === 'domains' || activeTab === 'generations' || activeTab === 'sales' || activeTab === 'editors');

  return (
    <AdminShell<AdminTab>
      scope="Sketchplan"
      role={adminRole}
      onBack={onClose}
      tabs={adminTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      variant="overlay"
      title={adminPageCopy[activeTab].title}
      description={adminPageCopy[activeTab].description}
      actions={canRefresh ? (
        <button
          type="button"
          onClick={refreshAdmin}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      ) : undefined}
    >

          {/* INVOICING TAB */}
          {isSuperAdmin && activeTab === 'invoicing' && (
            <div className="max-w-5xl mx-auto">
              <InvoicingTab />
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filter Bar */}
              <div className="flex items-center gap-4">
                <select
                  value={orderStatusFilter}
                  onChange={(e) => {
                    setOrderStatusFilter(e.target.value as OrderStatus | '');
                    setTimeout(loadOrders, 100);
                  }}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
                >
                  <option value="">All Orders</option>
                  <option value="awaiting_payment">Awaiting Payment</option>
                  <option value="pending_payment">Awaiting Payment (legacy)</option>
                  <option value="pending">Pending</option>
                  <option value="quoted">Quoted</option>
                  <option value="paid">Paid</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={loadOrders}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                >
                  <RefreshCw size={16} className={loadingOrders ? 'animate-spin' : ''} />
                  Refresh
                </button>
                <span className="text-sm text-slate-400 ml-auto">
                  {orders.length} orders
                </span>
              </div>

              {/* Orders Table */}
              {loadingOrders ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-teal-400" />
                </div>
              ) : orders.length === 0 ? (
                <div className="bg-slate-900 rounded-xl border border-slate-800 p-8 text-center">
                  <ClipboardList size={48} className="mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No orders found</p>
                </div>
              ) : (
                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Order</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Property</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Size</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Turnaround</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Due by</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-bold text-teal-400">
                              {order.job_number || '—'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-white max-w-[200px] truncate">{order.property_address}</div>
                            <div className="text-xs text-slate-500">{order.contact_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-white">{order.estimated_sq_ft?.toLocaleString() || '?'} sq ft</div>
                            <div className="text-xs text-slate-500">{order.floors} floor{order.floors !== 1 ? 's' : ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              order.turnaround === 'express' ? 'bg-amber-900/30 text-amber-400' : 'bg-slate-700 text-slate-300'
                            }`}>
                              {order.turnaround === 'express' ? '24hr Express' : '48hr Standard'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-xs ${order.due_at && new Date(order.due_at) < new Date() && order.status !== 'completed' ? 'text-amber-400' : 'text-slate-300'}`}>
                              {order.due_at ? new Date(order.due_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => { const s = badgeStyle(ORDER_STATUS_COLORS, order.status); return (
                            <span className={`text-xs font-medium px-2 py-1 rounded border ${s.bg} ${s.text}`}>
                              {s.label}
                            </span>
                            ); })()}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="text-teal-400 hover:text-teal-300 text-sm font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Selected Order Detail Modal */}
              {selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                  <div className="bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-slate-700">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-bold text-white">Order Details</h3>
                          {selectedOrder.job_number && (
                            <span className="px-2 py-1 bg-teal-600 text-white text-xs font-bold rounded">
                              {selectedOrder.job_number}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {new Date(selectedOrder.created_at).toLocaleString('en-GB')}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                      {/* Status & Actions */}
                      <div className="flex items-center gap-4">
                        {(() => { const s = badgeStyle(ORDER_STATUS_COLORS, selectedOrder.status); return (
                        <span className={`text-sm font-medium px-3 py-1.5 rounded border ${s.bg} ${s.text}`}>
                          {s.label}
                        </span>
                        ); })()}
                        
                        {selectedOrder.status === 'pending' && (
                          <button
                            onClick={() => updateOrderStatus(selectedOrder.id, 'in_progress')}
                            disabled={updatingOrder}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                          >
                            <Play size={16} />
                            Start Working
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            onClick={() => deleteOrder(selectedOrder.id)}
                            disabled={deletingOrderId === selectedOrder.id}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                          >
                            <Trash2 size={16} />
                            {deletingOrderId === selectedOrder.id ? 'Deleting...' : 'Delete Order'}
                          </button>
                        )}
                      </div>

                      {/* Order timer: ordered, due by, started (for prioritisation) */}
                      <div className="bg-slate-900 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Ordered</div>
                          <div className="text-white font-medium">
                            {new Date(selectedOrder.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Due by</div>
                          <div className={`font-medium ${selectedOrder.due_at && new Date(selectedOrder.due_at) < new Date() && selectedOrder.status !== 'completed' ? 'text-amber-400' : 'text-white'}`}>
                            {selectedOrder.due_at
                              ? new Date(selectedOrder.due_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Started</div>
                          <div className="text-white font-medium">
                            {selectedOrder.started_at
                              ? new Date(selectedOrder.started_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
                              : '—'}
                          </div>
                        </div>
                      </div>

                      {/* Property Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                            <MapPin size={14} />
                            Property
                          </div>
                          <div className="text-white font-medium">{selectedOrder.property_address}</div>
                          <div className="text-slate-400 text-sm mt-1">
                            {selectedOrder.estimated_sq_ft?.toLocaleString()} sq ft • {selectedOrder.floors} floor{selectedOrder.floors !== 1 ? 's' : ''} • {selectedOrder.property_type}
                          </div>
                        </div>
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                            <User size={14} />
                            Customer
                          </div>
                          <div className="text-white font-medium">{selectedOrder.contact_email}</div>
                          {selectedOrder.contact_phone && (
                            <div className="text-slate-400 text-sm mt-1">{selectedOrder.contact_phone}</div>
                          )}
                        </div>
                      </div>

                      {/* Notes */}
                      {selectedOrder.notes && (
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="text-slate-400 text-sm mb-2">Customer Notes</div>
                          <div className="text-white">{selectedOrder.notes}</div>
                        </div>
                      )}

                      {/* Sketch Download */}
                      <div className="bg-slate-900 rounded-xl p-4">
                        <div className="text-slate-400 text-sm mb-3">
                          Customer Sketch{allSketchDownloadUrls.length > 1 ? `es (${allSketchDownloadUrls.length})` : ''}
                        </div>
                        {selectedOrder.sketch_url ? (
                          <>
                          <div className="flex items-center gap-4">
                            {(() => {
                              const currentUrl = allSketchDownloadUrls[selectedSketchIdx] || sketchDownloadUrl;
                              return currentUrl && !currentUrl.startsWith('blob:') && !sketchLoadError ? (
                                <img
                                  src={currentUrl}
                                  alt={`Sketch ${selectedSketchIdx + 1}`}
                                  className="w-32 h-32 object-contain bg-white rounded-lg"
                                  onError={() => setSketchLoadError(true)}
                                />
                              ) : (
                                <div className="w-32 h-32 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center text-xs text-slate-400 text-center px-2">
                                  Sketch preview unavailable
                                </div>
                              );
                            })()}
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={async () => {
                                  const url = allSketchDownloadUrls[selectedSketchIdx] || sketchDownloadUrl || getFirstSketchUrl(selectedOrder.sketch_url);
                                  if (!url || url.startsWith('blob:')) return;
                                  try {
                                    const response = await fetch(url);
                                    const blob = await response.blob();
                                    const ext = url.includes('.png') ? 'png' : url.includes('.webp') ? 'webp' : 'jpg';
                                    const sanitizedAddress = selectedOrder.property_address.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
                                    const suffix = allSketchDownloadUrls.length > 1 ? `_${selectedSketchIdx + 1}` : '';
                                    const filename = `${selectedOrder.job_number || 'Sketch'}_${sanitizedAddress}${suffix}.${ext}`;
                                    const downloadUrl = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = downloadUrl;
                                    a.download = filename;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(downloadUrl);
                                  } catch (err) {
                                    console.error('Download failed:', err);
                                    window.open(url, '_blank');
                                  }
                                }}
                                disabled={(() => {
                                  const u = allSketchDownloadUrls[selectedSketchIdx] || sketchDownloadUrl || getFirstSketchUrl(selectedOrder.sketch_url);
                                  return !u || u.startsWith('blob:');
                                })()}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                  (() => {
                                    const u = allSketchDownloadUrls[selectedSketchIdx] || sketchDownloadUrl || getFirstSketchUrl(selectedOrder.sketch_url);
                                    return !u || u.startsWith('blob:')
                                      ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                      : 'bg-teal-600 hover:bg-teal-500 text-white';
                                  })()
                                }`}
                              >
                                <Download size={16} />
                                Download Sketch{allSketchDownloadUrls.length > 1 ? ` ${selectedSketchIdx + 1}` : ''}
                              </button>
                              {allSketchDownloadUrls.length > 1 && (
                                <button
                                  onClick={async () => {
                                    for (let i = 0; i < allSketchDownloadUrls.length; i++) {
                                      const url = allSketchDownloadUrls[i];
                                      if (!url || url.startsWith('blob:')) continue;
                                      try {
                                        const response = await fetch(url);
                                        const blob = await response.blob();
                                        const ext = url.includes('.png') ? 'png' : url.includes('.webp') ? 'webp' : 'jpg';
                                        const sanitizedAddress = selectedOrder.property_address.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
                                        const filename = `${selectedOrder.job_number || 'Sketch'}_${sanitizedAddress}_${i + 1}.${ext}`;
                                        const downloadUrl = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = downloadUrl;
                                        a.download = filename;
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        window.URL.revokeObjectURL(downloadUrl);
                                      } catch (err) {
                                        console.error(`Download failed for sketch ${i + 1}:`, err);
                                      }
                                    }
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 bg-teal-700 hover:bg-teal-600 text-white rounded-lg text-sm font-medium"
                                >
                                  <Download size={16} />
                                  Download All ({allSketchDownloadUrls.length})
                                </button>
                              )}
                              {(getFirstSketchUrl(selectedOrder.sketch_url) ?? '').startsWith('blob:') && (
                                <div className="text-xs text-amber-400">
                                  This is a legacy upload and isn’t accessible outside the original browser.
                                </div>
                              )}
                              {sketchLoadError && !(getFirstSketchUrl(selectedOrder.sketch_url) ?? '').startsWith('blob:') && (
                                <div className="text-xs text-amber-400">
                                  Preview failed. Use the download button instead.
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  const allUrls = getAllSketchUrls(selectedOrder.sketch_url);
                                  const text = allUrls.join('\n');
                                  navigator.clipboard.writeText(text);
                                  alert(`${allUrls.length} sketch URL(s) copied:\n\n${text}`);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
                              >
                                Copy URL{getAllSketchUrls(selectedOrder.sketch_url).length > 1 ? 's' : ''} (Debug)
                              </button>
                            </div>
                          </div>
                          {/* Thumbnail strip for multiple sketches */}
                          {allSketchDownloadUrls.length > 1 && (
                            <div className="flex gap-2 mt-3">
                              {allSketchDownloadUrls.map((url, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => { setSelectedSketchIdx(idx); setSketchLoadError(false); }}
                                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                                    selectedSketchIdx === idx
                                      ? 'border-teal-500 ring-1 ring-teal-500/50'
                                      : 'border-slate-700 hover:border-slate-500'
                                  }`}
                                >
                                  {url && !url.startsWith('blob:') ? (
                                    <img src={url} alt={`Sketch ${idx + 1}`} className="w-full h-full object-contain bg-white" />
                                  ) : (
                                    <div className="w-full h-full bg-slate-800 flex items-center justify-center text-xs text-slate-500">{idx + 1}</div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          {/* Debug info */}
                          <div className="mt-3 text-xs text-slate-500 bg-slate-800 p-2 rounded overflow-x-auto max-w-full break-all">
                            <div><strong>Stored:</strong> {selectedOrder.sketch_url}</div>
                            <div className="mt-1"><strong>Signed:</strong> {allSketchDownloadUrls.length > 0 ? allSketchDownloadUrls.join(', ') : 'Not generated'}</div>
                          </div>
                          </>
                        ) : (
                          <div className="text-sm text-slate-400">
                            No sketch uploaded for this order yet.
                          </div>
                        )}
                      </div>

                      {/* Upload Output */}
                      <div className="bg-slate-900 rounded-xl p-4">
                        <div className="text-slate-400 text-sm mb-3">Completed Floor Plan{getAllSketchUrls(selectedOrder.output_url).length > 1 ? 's' : ''}</div>

                        {(() => {
                          const outputUrls = getAllSketchUrls(selectedOrder.output_url);
                          return (
                            <>
                              {outputUrls.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                  {outputUrls.map((url, idx) => (
                                    <div key={idx} className="relative group">
                                      <img
                                        src={url}
                                        alt={`Output ${idx + 1}`}
                                        className="w-full h-28 object-contain bg-white rounded-lg"
                                      />
                                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <a
                                          href={url}
                                          download
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 bg-slate-800/80 hover:bg-slate-700 text-white rounded"
                                          title="Download"
                                        >
                                          <Download size={14} />
                                        </a>
                                        <button
                                          onClick={() => removeOutputFile(selectedOrder.id, url)}
                                          className="p-1 bg-red-600/80 hover:bg-red-500 text-white rounded"
                                          title="Remove"
                                        >
                                          <XCircle size={14} />
                                        </button>
                                      </div>
                                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                                        {idx + 1}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="flex items-center gap-3 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium cursor-pointer">
                                  <Upload size={16} />
                                  {uploadingOutput ? 'Uploading...' : outputUrls.length > 0 ? 'Add More Files' : 'Upload Completed Floor Plan'}
                                  <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                      const files = e.target.files;
                                      if (files && files.length > 0) {
                                        uploadOutputFiles(selectedOrder.id, Array.from(files));
                                      }
                                    }}
                                    disabled={uploadingOutput}
                                  />
                                </label>
                                {outputUrls.length > 0 && (
                                  <button
                                    onClick={() => sendDeliveryEmail(selectedOrder)}
                                    className="flex items-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium"
                                  >
                                    <Send size={16} />
                                    Send to Customer
                                  </button>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ACTIVITY FEED TAB */}
          {isSuperAdmin && activeTab === 'activity' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                  <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter size={16} className="text-slate-400" />
                    <select
                      value={activityFilter}
                      onChange={(e) => {
                        setActivityFilter(e.target.value);
                        setTimeout(loadActivityFeed, 100);
                      }}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    >
                      <option value="">All Events</option>
                      <option value="magic_link_requested">Magic Link</option>
                      <option value="login">Login</option>
                      <option value="generation_started">Generation Started</option>
                      <option value="generation_completed">Generation Complete</option>
                      <option value="generation_failed">Generation Failed</option>
                      <option value="credits_granted">Credits Granted</option>
                      <option value="payment_completed">Payment</option>
                      <option value="promo_redeemed">Promo Redeemed</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-slate-400" />
                    <span className="text-xs text-slate-500">App:</span>
                    <div className="flex gap-1">
                      {['', 'photoclear', 'staging', 'sketchplan'].map((app) => (
                        <button
                          key={app || 'all'}
                          onClick={() => setActivityAppFilter(app)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                            activityAppFilter === app
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                          }`}
                        >
                          {app || 'All'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-slate-500">
                    {activityTotal} total events
                  </span>
                </div>
              </div>

              {/* Event List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingActivity ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : activityEvents.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {(activityAppFilter
                      ? activityEvents.filter((e) => (e.metadata as Record<string, string> | null)?.source_app === activityAppFilter)
                      : activityEvents
                    ).map((event) => {
                      const typeInfo = EVENT_TYPE_LABELS[event.event_type] || { label: event.event_type, color: 'bg-slate-700 text-slate-400' };
                      const sourceApp = (event.metadata as Record<string, string> | null)?.source_app;
                      return (
                        <div
                          key={event.id}
                          className="p-4 hover:bg-slate-800/50 cursor-pointer transition-colors"
                          onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              {sourceApp && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 whitespace-nowrap">
                                  {sourceApp}
                                </span>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-white truncate">
                                  {event.user_email || 'Unknown user'}
                                </p>
                                {event.ip_address && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    IP: {event.ip_address}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs text-slate-400">
                                {new Date(event.created_at).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          {selectedEvent?.id === event.id && event.metadata && Object.keys(event.metadata).length > 0 && (
                            <div className="mt-3 p-3 bg-black rounded-lg">
                              <p className="text-xs text-slate-500 mb-2">Metadata:</p>
                              <pre className="text-xs text-slate-300 overflow-x-auto">
                                {JSON.stringify(event.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Activity size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No events yet</h4>
                    <p className="text-sm text-slate-500">Events will appear here as they occur</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* USERS & STATUS TAB */}
          {isSuperAdmin && activeTab === 'users' && !selectedUserDetail && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by email..."
                      className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500"
                      onKeyDown={(e) => e.key === 'Enter' && loadUsersWithStatus()}
                    />
                  </div>
                  <select
                    value={userStatusFilter}
                    onChange={(e) => {
                      setUserStatusFilter(e.target.value as UserRiskStatus | '');
                      setTimeout(loadUsersWithStatus, 100);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="ok">OK</option>
                    <option value="watch">Watch</option>
                    <option value="restricted">Restricted</option>
                    <option value="banned">Banned</option>
                  </select>
                  <button
                    onClick={loadUsersWithStatus}
                    className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium text-sm transition-colors"
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-3">{usersTotal} users found</p>
              </div>

              {/* Users List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingUsers ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : usersWithStatus.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {usersWithStatus.map((u) => {
                      const statusInfo = badgeStyle(STATUS_COLORS, u.risk_status);
                      return (
                        <div key={u.id} className="p-4 hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-white truncate">{u.email}</p>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusInfo.bg} ${statusInfo.text}`}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                  <span>{u.credits} credits</span>
                                  <button
                                    type="button"
                                    onClick={() => void openHubAdminUser(supabase, u.id)}
                                    className="text-sky-400 hover:text-sky-300"
                                  >
                                    Hub account
                                  </button>
                                  <span>{u.event_count} events</span>
                                  {u.last_activity && (
                                    <span>Last: {new Date(u.last_activity).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => loadUserDetail(u.id)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                                title="View Timeline"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => {
                                  setStatusChangeUser(u);
                                  setNewStatus(u.risk_status);
                                  setStatusReason(u.status_reason || '');
                                }}
                                className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                                title="Change Status"
                              >
                                <Edit3 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Users size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No users found</h4>
                    <p className="text-sm text-slate-500">Try adjusting your search</p>
                  </div>
                )}
              </div>

              {/* Status Change Modal */}
              {statusChangeUser && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-4">Change User Status</h3>
                    <p className="text-sm text-slate-400 mb-4">{statusChangeUser.email}</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value as UserRiskStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal access</option>
                          <option value="watch">Watch - Monitor activity</option>
                          <option value="restricted">Restricted - Limited access</option>
                          <option value="banned">Banned - No access</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={statusReason}
                          onChange={(e) => setStatusReason(e.target.value)}
                          placeholder="Reason for status change"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setStatusChangeUser(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateUserStatus}
                        disabled={updatingStatus}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingStatus ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* USER DETAIL VIEW */}
          {isSuperAdmin && activeTab === 'users' && selectedUserDetail && (
            <div className="max-w-5xl mx-auto space-y-6">
              <button
                onClick={() => {
                  setSelectedUserDetail(null);
                  setUserDetailTab('timeline');
                  setUserGenerations([]);
                  setUserCreditLedger([]);
                }}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Users
              </button>

              {/* User Info Card */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-white">{selectedUserDetail.user.email}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      {(() => { const s = badgeStyle(STATUS_COLORS, selectedUserDetail.user.risk_status); return (
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${s.bg} ${s.text}`}>
                        {s.label}
                      </span>
                      ); })()}
                      <span className="text-sm text-teal-400 flex items-center gap-1">
                        <Coins size={14} />
                        {selectedUserDetail.user.credits} credits
                      </span>
                      <button
                        type="button"
                        onClick={() => void openHubAdminUser(supabase, selectedUserDetail.user.id)}
                        className="text-sm text-sky-400 hover:text-sky-300"
                      >
                        Free HDR edits in Hub
                      </button>
                    </div>
                    {selectedUserDetail.user.status_reason && (
                      <p className="text-sm text-slate-500 mt-2">Reason: {selectedUserDetail.user.status_reason}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-500 block">
                      Joined: {new Date(selectedUserDetail.user.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => setShowAdjustCredits(true)}
                      className="mt-2 text-xs bg-teal-600 hover:bg-teal-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <CreditCard size={12} />
                      Adjust Credits
                    </button>
                  </div>
                </div>
              </div>

              {/* Billing & invoicing */}
              <UserBillingCard
                userId={selectedUserDetail.user.id}
                userEmail={selectedUserDetail.user.email}
              />

              {/* Tab Navigation */}
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-xl p-2">
                <button
                  onClick={() => setUserDetailTab('timeline')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'timeline' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Activity size={16} className="inline mr-2" />
                  Timeline
                </button>
                <button
                  onClick={() => setUserDetailTab('generations')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'generations' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Image size={16} className="inline mr-2" />
                  Gallery ({userGenerations.length})
                </button>
                <button
                  onClick={() => setUserDetailTab('credits')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'credits' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Coins size={16} className="inline mr-2" />
                  Credit Ledger
                </button>
              </div>

              {/* Timeline Tab */}
              {userDetailTab === 'timeline' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h4 className="font-semibold text-white">Activity Timeline</h4>
                    <p className="text-sm text-slate-500">{selectedUserDetail.events.length} events</p>
                  </div>
                  {loadingUserDetail ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                    </div>
                  ) : selectedUserDetail.events.length > 0 ? (
                    <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
                      {selectedUserDetail.events.map((event) => {
                        const typeInfo = EVENT_TYPE_LABELS[event.event_type] || { label: event.event_type, color: 'bg-slate-700 text-slate-400' };
                        return (
                          <div key={event.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(event.created_at).toLocaleString()}
                              </span>
                            </div>
                            {event.ip_address && (
                              <p className="text-xs text-slate-500 mt-2">IP: {event.ip_address}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-slate-500">No activity recorded</p>
                    </div>
                  )}
                </div>
              )}

              {/* Generations Gallery Tab */}
              {userDetailTab === 'generations' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h4 className="font-semibold text-white">Generated Images</h4>
                    <p className="text-sm text-slate-500">{userGenerations.length} generations</p>
                  </div>
                  {loadingUserGenerations ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                    </div>
                  ) : userGenerations.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
                      {userGenerations.map((gen) => {
                        const statusInfo = GENERATION_STATUS_COLORS[gen.status as GenerationStatus] || GENERATION_STATUS_COLORS.queued;
                        const outputUrl = gen.output_image_path ? getImageUrl(gen.output_image_path) : null;
                        const inputUrl = gen.input_asset_path ? getImageUrl(gen.input_asset_path) : null;
                        return (
                          <div key={gen.id} className="bg-black rounded-xl overflow-hidden border border-slate-800">
                            {/* Side by side images */}
                            <div className="grid grid-cols-2 gap-px bg-slate-800">
                              {/* Original */}
                              <div className="relative aspect-[4/3] bg-slate-900">
                                {inputUrl ? (
                                  <a href={inputUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={getThumbnailUrl(inputUrl, { width: 200, height: 150, quality: 70 }) ?? inputUrl}
                                      alt="Original"
                                      className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                                      loading="lazy"
                                      onError={(e) => { const el = e.currentTarget; if (el.src !== inputUrl) el.src = inputUrl; }}
                                    />
                                  </a>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image size={24} className="text-slate-700" />
                                  </div>
                                )}
                                <span className="absolute top-2 left-2 bg-black/70 text-slate-300 text-[10px] font-medium px-2 py-0.5 rounded">
                                  Original
                                </span>
                              </div>
                              {/* Processed */}
                              <div className="relative aspect-[4/3] bg-slate-900">
                                {outputUrl ? (
                                  <a href={outputUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={getThumbnailUrl(outputUrl, { width: 200, height: 150, quality: 70 }) ?? outputUrl}
                                      alt="Processed"
                                      className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                                      loading="lazy"
                                      onError={(e) => { const el = e.currentTarget; if (el.src !== outputUrl) el.src = outputUrl; }}
                                    />
                                  </a>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image size={24} className="text-slate-700" />
                                  </div>
                                )}
                                <span className="absolute top-2 left-2 bg-teal-600/90 text-white text-[10px] font-medium px-2 py-0.5 rounded">
                                  Processed
                                </span>
                                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                            </div>
                            <div className="p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-slate-400">
                                  {new Date(gen.created_at).toLocaleDateString()}
                                </p>
                                {gen.credits_used > 0 && (
                                  <span className="text-xs text-teal-400">{gen.credits_used} cr</span>
                                )}
                              </div>
                              {gen.error_message && (
                                <p className="text-xs text-red-400 mt-1 truncate" title={gen.error_message}>
                                  {gen.error_message}
                                </p>
                              )}
                              <div className="flex items-center gap-1 mt-2">
                                {inputUrl && (
                                  <button
                                    onClick={() => handleDownload(inputUrl, `${gen.id}-original`)}
                                    disabled={downloadingId === `${gen.id}-original`}
                                    className="flex-1 text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1 bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                    title="Download Original"
                                  >
                                    {downloadingId === `${gen.id}-original` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                    Original
                                  </button>
                                )}
                                {outputUrl && (
                                  <button
                                    onClick={() => handleDownload(outputUrl, gen.id)}
                                    disabled={downloadingId === gen.id}
                                    className="flex-1 text-xs text-teal-400 hover:text-teal-300 flex items-center justify-center gap-1 bg-teal-900/30 hover:bg-teal-900/50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                    title="Download Processed"
                                  >
                                    {downloadingId === gen.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                    Result
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Image size={48} className="mx-auto text-slate-700 mb-4" />
                      <p className="text-sm text-slate-500">No generations yet</p>
                    </div>
                  )}
                </div>
              )}

              {/* Credit Ledger Tab */}
              {userDetailTab === 'credits' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-white">Credit Ledger</h4>
                      <p className="text-sm text-slate-500">
                        Current Balance: <span className="text-teal-400 font-semibold">{userCreditBalance}</span>
                      </p>
                    </div>
                  </div>
                  {loadingCreditLedger ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                    </div>
                  ) : userCreditLedger.length > 0 ? (
                    <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
                      {userCreditLedger.map((entry) => (
                        <div key={entry.id} className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">{entry.reason}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {entry.ref_type && (
                                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-400">
                                  {entry.ref_type}
                                </span>
                              )}
                              <span className="text-xs text-slate-500">
                                {new Date(entry.created_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <span className={`text-lg font-bold ${
                            entry.delta > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {entry.delta > 0 ? '+' : ''}{entry.delta}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Coins size={48} className="mx-auto text-slate-700 mb-4" />
                      <p className="text-sm text-slate-500">No credit history</p>
                    </div>
                  )}
                </div>
              )}

              {/* Adjust Credits Modal */}
              {showAdjustCredits && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-4">Adjust Credits</h3>
                    <p className="text-sm text-slate-400 mb-4">{selectedUserDetail.user.email}</p>
                    <p className="text-sm text-slate-400 mb-4">
                      Current balance: <span className="text-teal-400 font-semibold">{selectedUserDetail.user.credits}</span>
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                        <input
                          type="number"
                          value={adjustCreditsAmount}
                          onChange={(e) => setAdjustCreditsAmount(e.target.value)}
                          placeholder="e.g., 10 or -5"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                        <p className="text-xs text-slate-500 mt-1">Positive to add, negative to deduct</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason *</label>
                        <input
                          type="text"
                          value={adjustCreditsReason}
                          onChange={(e) => setAdjustCreditsReason(e.target.value)}
                          placeholder="Reason for adjustment"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => {
                          setShowAdjustCredits(false);
                          setAdjustCreditsAmount('');
                          setAdjustCreditsReason('');
                        }}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAdjustCredits}
                        disabled={adjustingCredits || !adjustCreditsAmount || !adjustCreditsReason.trim()}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {adjustingCredits ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Adjust
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* IP ANALYTICS TAB */}
          {isSuperAdmin && activeTab === 'ips' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {ipError && (
                <div className="bg-red-900/50 border border-red-700 rounded-xl p-4 text-red-200 text-sm">
                  <strong>Error loading IP analytics:</strong> {ipError}
                  <p className="mt-1 text-red-300/70">Run the migration <code className="bg-red-900 px-1 rounded">20260319100000_fix_ip_logging_and_analytics.sql</code> in the Supabase SQL Editor.</p>
                </div>
              )}
              {/* World Map */}
              <IPWorldMap ipAnalytics={ipAnalytics} accentColor="#14b8a6" />

              {/* IP List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h4 className="font-semibold text-white">Top IPs by Email Count</h4>
                  <select
                    value={ipDays}
                    onChange={(e) => {
                      const d = parseInt(e.target.value);
                      setIpDays(d);
                      loadIPAnalytics(d);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 6 months</option>
                    <option value={0}>All time</option>
                  </select>
                </div>
                {loadingIPs ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : ipAnalytics.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {ipAnalytics.map((ip) => {
                      const controlInfo = badgeStyle(CONTROL_COLORS, ip.control_status);
                      const emailCount = ip.email_count || 0;
                      const isSuspicious = emailCount > 3 || ip.user_count > 3;
                      const isHighRisk = emailCount > 5;
                      return (
                        <div key={ip.ip_address} className="p-4 hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-sm text-white font-mono">{ip.ip_address}</code>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${controlInfo.bg} ${controlInfo.text}`}>
                                  {controlInfo.label}
                                </span>
                                {isHighRisk ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-900/40 text-red-300 border border-red-700 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    High Risk — {emailCount} emails
                                  </span>
                                ) : isSuspicious && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-800 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    Suspicious — {emailCount} emails
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                <span>{ip.event_count} events</span>
                                <span>{ip.user_count} logged-in users</span>
                                <span className={emailCount > ip.user_count ? 'text-yellow-500' : ''}>{emailCount} emails</span>
                                <span>First: {new Date(ip.first_activity).toLocaleDateString()}</span>
                                <span>Last: {new Date(ip.last_activity).toLocaleDateString()}</span>
                              </div>
                              {(ip.emails?.length ?? 0) > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {ip.emails!.map((email) => (
                                    <span
                                      key={email}
                                      className="px-2 py-0.5 rounded-md bg-black/40 border border-slate-700 text-[11px] text-slate-200 font-mono break-all"
                                    >
                                      {email}
                                    </span>
                                  ))}
                                  {emailCount > ip.emails!.length && (
                                    <span className="px-2 py-0.5 text-[11px] text-slate-500">
                                      +{emailCount - ip.emails!.length} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setSelectedIP(ip);
                                setIpControlStatus(ip.control_status);
                                setIpThrottle(ip.throttle_multiplier.toString());
                                setIpControlReason(ip.control_reason || '');
                              }}
                              className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                              title="Manage IP"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Wifi size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No IP data yet</h4>
                    <p className="text-sm text-slate-500">IP analytics will appear as events are logged</p>
                  </div>
                )}
              </div>

              {/* IP Control Modal */}
              {selectedIP && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-2">IP Control</h3>
                    <code className="text-sm text-teal-400 font-mono">{selectedIP.ip_address}</code>
                    {(selectedIP.emails?.length ?? 0) > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-400 mb-1.5">Emails on this IP</p>
                        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                          {selectedIP.emails!.map((email) => (
                            <span
                              key={email}
                              className="px-2 py-0.5 rounded-md bg-black/40 border border-slate-700 text-[11px] text-slate-200 font-mono break-all"
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={ipControlStatus}
                          onChange={(e) => setIpControlStatus(e.target.value as ControlStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal</option>
                          <option value="throttled">Throttled - Rate limited</option>
                          <option value="blocked">Blocked - No access</option>
                        </select>
                      </div>
                      {ipControlStatus === 'throttled' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Throttle Multiplier</label>
                          <input
                            type="number"
                            min="0.1"
                            max="10"
                            step="0.1"
                            value={ipThrottle}
                            onChange={(e) => setIpThrottle(e.target.value)}
                            className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                          />
                          <p className="text-xs text-slate-500 mt-1">Higher = stricter limit (2.0 = half the normal rate)</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={ipControlReason}
                          onChange={(e) => setIpControlReason(e.target.value)}
                          placeholder="Reason for control"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setSelectedIP(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateIPControl}
                        disabled={updatingIP}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingIP ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DOMAIN ANALYTICS TAB */}
          {isSuperAdmin && activeTab === 'domains' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Domain List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800">
                  <h4 className="font-semibold text-white">Top Domains (Last 30 Days)</h4>
                </div>
                {loadingDomains ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : domainAnalytics.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {domainAnalytics.map((domain) => {
                      const controlInfo = badgeStyle(CONTROL_COLORS, domain.control_status);
                      return (
                        <div key={domain.domain} className="p-4 hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-white font-medium">@{domain.domain}</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${controlInfo.bg} ${controlInfo.text}`}>
                                  {controlInfo.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                <span>{domain.user_count} users</span>
                                <span>{domain.event_count} events</span>
                                <span>Last: {new Date(domain.last_activity).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setSelectedDomain(domain);
                                setDomainControlStatus(domain.control_status);
                                setDomainThrottle(domain.throttle_multiplier.toString());
                                setDomainControlReason(domain.control_reason || '');
                              }}
                              className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                              title="Manage Domain"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Globe size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No domain data yet</h4>
                    <p className="text-sm text-slate-500">Domain analytics will appear as events are logged</p>
                  </div>
                )}
              </div>

              {/* Domain Control Modal */}
              {selectedDomain && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-2">Domain Control</h3>
                    <span className="text-sm text-teal-400">@{selectedDomain.domain}</span>
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={domainControlStatus}
                          onChange={(e) => setDomainControlStatus(e.target.value as DomainControlStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal</option>
                          <option value="throttled">Throttled - Rate limited</option>
                          <option value="require_payment">Require Payment - No free credits</option>
                          <option value="blocked">Blocked - No signups</option>
                        </select>
                      </div>
                      {domainControlStatus === 'throttled' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Throttle Multiplier</label>
                          <input
                            type="number"
                            min="0.1"
                            max="10"
                            step="0.1"
                            value={domainThrottle}
                            onChange={(e) => setDomainThrottle(e.target.value)}
                            className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                          />
                          <p className="text-xs text-slate-500 mt-1">Higher = stricter limit</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={domainControlReason}
                          onChange={(e) => setDomainControlReason(e.target.value)}
                          placeholder="Reason for control"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setSelectedDomain(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateDomainControl}
                        disabled={updatingDomain}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingDomain ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PROMO CODES TAB */}
          {isSuperAdmin && activeTab === 'promos' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Codes</p>
                      <p className="text-3xl font-bold text-white mt-1">{promoCodes.length}</p>
                    </div>
                    <Gift size={32} className="text-teal-400" />
                  </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Active Codes</p>
                      <p className="text-3xl font-bold text-green-400 mt-1">
                        {promoCodes.filter(p => p.active && (!p.expires_at || new Date(p.expires_at) > new Date())).length}
                      </p>
                    </div>
                    <CheckCircle size={32} className="text-green-400" />
                  </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Redemptions</p>
                      <p className="text-3xl font-bold text-teal-400 mt-1">{redemptions.length}</p>
                    </div>
                    <TrendingUp size={32} className="text-teal-400" />
                  </div>
                </div>
              </div>

              {/* Create Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreatePromo(!showCreatePromo)}
                  className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold transition-colors"
                >
                  {showCreatePromo ? <XCircle size={18} /> : <Plus size={18} />}
                  {showCreatePromo ? 'Cancel' : 'Create Promo Code'}
                </button>
              </div>

              {/* Create Form */}
              {showCreatePromo && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-6">Create New Promo Code</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Code *</label>
                      <input
                        type="text"
                        value={newPromoCode}
                        onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                        placeholder="e.g., WELCOME50"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500 uppercase tracking-wide"
                      />
                      <p className="text-xs text-slate-500 mt-1">3-20 alphanumeric characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Credits *</label>
                      <input
                        type="number"
                        min="1"
                        value={newPromoCredits}
                        onChange={(e) => setNewPromoCredits(e.target.value)}
                        placeholder="e.g., 10"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Max Uses</label>
                      <input
                        type="number"
                        min="1"
                        value={newPromoMaxUses}
                        onChange={(e) => setNewPromoMaxUses(e.target.value)}
                        placeholder="Leave empty for unlimited"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Expires At</label>
                      <input
                        type="datetime-local"
                        value={newPromoExpires}
                        onChange={(e) => setNewPromoExpires(e.target.value)}
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                      <input
                        type="text"
                        value={newPromoDescription}
                        onChange={(e) => setNewPromoDescription(e.target.value)}
                        placeholder="Optional internal note"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleCreatePromo}
                    disabled={creatingPromo || !newPromoCode || !newPromoCredits}
                    className="w-full mt-6 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    {creatingPromo ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                    Create Promo Code
                  </button>

                  {promoMessage && (
                    <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 text-sm ${promoMessage.type === 'success' ? 'bg-green-900/30 border border-green-800 text-green-400' : 'bg-red-900/30 border border-red-800 text-red-400'}`}>
                      {promoMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                      {promoMessage.text}
                    </div>
                  )}
                </div>
              )}

              {/* Promo Codes List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">All Promo Codes</h3>
                </div>

                {loadingPromos ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : promoCodes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-black/50 border-b border-slate-800 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-4">Code</th>
                          <th className="p-4">Credits</th>
                          <th className="p-4">Usage</th>
                          <th className="p-4">Expires</th>
                          <th className="p-4">Status</th>
                          <th className="p-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                        {promoCodes.map((promo) => {
                          const status = getPromoStatus(promo);
                          const isEditing = editingPromoId === promo.id;
                          
                          if (isEditing) {
                            return (
                              <tr key={promo.id} className="bg-slate-800/50">
                                <td colSpan={6} className="p-6">
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="text-white font-semibold">Edit Promo Code: <code className="text-teal-400">{promo.code}</code></h4>
                                      <button
                                        onClick={handleCancelEditPromo}
                                        className="text-slate-400 hover:text-white"
                                      >
                                        <XCircle size={20} />
                                      </button>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Credits *</label>
                                        <input
                                          type="number"
                                          value={editPromoCredits}
                                          onChange={(e) => setEditPromoCredits(e.target.value)}
                                          min="1"
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Max Uses</label>
                                        <input
                                          type="number"
                                          value={editPromoMaxUses}
                                          onChange={(e) => setEditPromoMaxUses(e.target.value)}
                                          placeholder="Unlimited"
                                          min="0"
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited</p>
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Expires At</label>
                                        <input
                                          type="date"
                                          value={editPromoExpires}
                                          onChange={(e) => setEditPromoExpires(e.target.value)}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Leave empty for no expiration</p>
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                                        <select
                                          value={editPromoActive ? 'active' : 'inactive'}
                                          onChange={(e) => setEditPromoActive(e.target.value === 'active')}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        >
                                          <option value="active">Active</option>
                                          <option value="inactive">Inactive</option>
                                        </select>
                                      </div>
                                      
                                      <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                                        <textarea
                                          value={editPromoDescription}
                                          onChange={(e) => setEditPromoDescription(e.target.value)}
                                          placeholder="Optional description"
                                          rows={2}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                      </div>
                                    </div>
                                    
                                    <div className="flex gap-3 pt-2">
                                      <button
                                        onClick={() => handleUpdatePromo(promo.id)}
                                        disabled={updatingPromo || !editPromoCredits}
                                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                                      >
                                        {updatingPromo ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                        Save Changes
                                      </button>
                                      <button
                                        onClick={handleCancelEditPromo}
                                        disabled={updatingPromo}
                                        className="px-6 bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-semibold transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          
                          return (
                            <tr key={promo.id} className="hover:bg-slate-800/50">
                              <td className="p-4">
                                <code className="text-teal-400 font-mono font-semibold">{promo.code}</code>
                                {promo.description && (
                                  <p className="text-xs text-slate-500 mt-1">{promo.description}</p>
                                )}
                              </td>
                              <td className="p-4 font-bold text-white">{promo.credits}</td>
                              <td className="p-4">
                                <span className="text-slate-400">{promo.current_uses}</span>
                                {promo.max_uses && (
                                  <span className="text-slate-500"> / {promo.max_uses}</span>
                                )}
                                {!promo.max_uses && <span className="text-slate-500"> / ∞</span>}
                              </td>
                              <td className="p-4 text-slate-400 text-xs">
                                {promo.expires_at 
                                  ? new Date(promo.expires_at).toLocaleDateString()
                                  : 'Never'
                                }
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                  {status.label}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleStartEditPromo(promo)}
                                    className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 transition-colors"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleTogglePromo(promo.id, promo.active)}
                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                      promo.active 
                                        ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                                        : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                                    }`}
                                  >
                                    {promo.active ? 'Deactivate' : 'Activate'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Gift size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No promo codes yet</h4>
                    <p className="text-sm text-slate-500">Create your first promo code to get started</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AUDIT LOG TAB */}
          {isSuperAdmin && activeTab === 'audit' && (
            <div className="max-w-6xl mx-auto">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">Recent Admin Actions</h3>
                  <p className="text-sm text-slate-500 mt-1">Last 50 administrative actions</p>
                </div>

                {loadingAudit ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : auditLog.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-black/50 border-b border-slate-800 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-4">Date</th>
                          <th className="p-4">Action</th>
                          <th className="p-4">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                        {auditLog.map((action) => (
                          <tr key={action.id} className="hover:bg-slate-800/50">
                            <td className="p-4 text-slate-400 whitespace-nowrap">
                              {new Date(action.created_at).toLocaleDateString()}{' '}
                              <span className="text-xs opacity-70">
                                {new Date(action.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                action.action_type === 'grant_credits' 
                                  ? 'bg-green-900/30 text-green-400' 
                                  : 'bg-blue-900/30 text-blue-400'
                              }`}>
                                {action.action_type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4">
                              {action.action_type === 'grant_credits' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">+{action.details.amount} credits to {action.details.target_email}</p>
                                  <p className="text-slate-500 mt-1">{action.details.reason}</p>
                                </div>
                              )}
                              {action.action_type === 'create_promo' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">Created code: <code className="text-teal-400">{action.details.code}</code></p>
                                  <p className="text-slate-500 mt-1">{action.details.credits} credits</p>
                                </div>
                              )}
                              {action.action_type === 'update_promo' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">Updated <code className="text-teal-400">{action.details.code}</code></p>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Activity size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No activity yet</h4>
                    <p className="text-sm text-slate-500">Admin actions will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GENERATIONS TAB */}
          {isSuperAdmin && activeTab === 'generations' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Stuck Generations Alert */}
              {stuckCount > 0 && (
                <div className="bg-yellow-900/20 border border-yellow-800 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle size={24} className="text-yellow-400" />
                      <div>
                        <h4 className="text-yellow-400 font-medium">{stuckCount} Stuck Generation{stuckCount !== 1 ? 's' : ''}</h4>
                        <p className="text-sm text-yellow-400/70">Generations stuck in "started" status for more than 15 minutes</p>
                      </div>
                    </div>
                    <button
                      onClick={handleCleanupStuck}
                      disabled={cleaningStuck}
                      className="bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                    >
                      {cleaningStuck ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Cleanup & Refund
                    </button>
                  </div>
                  {cleanupResult && (
                    <div className="mt-3 p-3 bg-green-900/30 border border-green-800 rounded-lg">
                      <p className="text-sm text-green-400">
                        ✓ Cleaned {cleanupResult.cleaned} generation{cleanupResult.cleaned !== 1 ? 's' : ''}, refunded {cleanupResult.refunded} credit{cleanupResult.refunded !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={generationEmailFilter}
                      onChange={(e) => setGenerationEmailFilter(e.target.value)}
                      placeholder="Filter by user email..."
                      className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500"
                      onKeyDown={(e) => e.key === 'Enter' && loadGenerations()}
                    />
                  </div>
                  <select
                    value={generationStatusFilter}
                    onChange={(e) => {
                      setGenerationStatusFilter(e.target.value as GenerationStatus | '');
                      setGenerationsPage(0);
                      setTimeout(loadGenerations, 100);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="queued">Queued</option>
                    <option value="started">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                  <button
                    onClick={() => {
                      setGenerationsPage(0);
                      loadGenerations();
                    }}
                    className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium text-sm transition-colors"
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-400">From:</label>
                    <input
                      type="date"
                      value={generationDateFrom}
                      onChange={(e) => setGenerationDateFrom(e.target.value)}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-400">To:</label>
                    <input
                      type="date"
                      value={generationDateTo}
                      onChange={(e) => setGenerationDateTo(e.target.value)}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  {(generationDateFrom || generationDateTo) && (
                    <button
                      onClick={() => {
                        setGenerationDateFrom('');
                        setGenerationDateTo('');
                        setGenerationsPage(0);
                        setTimeout(loadGenerations, 100);
                      }}
                      className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      Clear dates
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-3">{generationsTotal} total generations</p>
              </div>

              {/* Generations Grid */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingGenerations ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                  </div>
                ) : generations.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
                      {generations.map((gen) => {
                        const statusInfo = GENERATION_STATUS_COLORS[gen.status as GenerationStatus] || GENERATION_STATUS_COLORS.queued;
                        const outputUrl = gen.output_image_path ? getImageUrl(gen.output_image_path) : null;
                        const inputUrl = gen.input_asset_path ? getImageUrl(gen.input_asset_path) : null;
                        return (
                          <div key={gen.id} className="bg-black rounded-xl overflow-hidden border border-slate-800 hover:border-slate-700 transition-colors">
                            {/* Side by side images */}
                            <div className="grid grid-cols-2 gap-px bg-slate-800">
                              {/* Original */}
                              <div className="relative aspect-[4/3] bg-slate-900">
                                {inputUrl ? (
                                  <a href={inputUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={getThumbnailUrl(inputUrl, { width: 200, height: 150, quality: 70 }) ?? inputUrl}
                                      alt="Original"
                                      className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                                      loading="lazy"
                                      onError={(e) => { const el = e.currentTarget; if (el.src !== inputUrl) el.src = inputUrl; }}
                                    />
                                  </a>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image size={24} className="text-slate-700" />
                                  </div>
                                )}
                                <span className="absolute top-2 left-2 bg-black/70 text-slate-300 text-[10px] font-medium px-2 py-0.5 rounded">
                                  Original
                                </span>
                              </div>
                              {/* Processed */}
                              <div className="relative aspect-[4/3] bg-slate-900">
                                {outputUrl ? (
                                  <a href={outputUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={getThumbnailUrl(outputUrl, { width: 200, height: 150, quality: 70 }) ?? outputUrl}
                                      alt="Processed"
                                      className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                                      loading="lazy"
                                      onError={(e) => { const el = e.currentTarget; if (el.src !== outputUrl) el.src = outputUrl; }}
                                    />
                                  </a>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image size={24} className="text-slate-700" />
                                  </div>
                                )}
                                <span className="absolute top-2 left-2 bg-teal-600/90 text-white text-[10px] font-medium px-2 py-0.5 rounded">
                                  Processed
                                </span>
                                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                            </div>
                            <div className="p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-white truncate" title={gen.user_email || 'Unknown'}>
                                  {gen.user_email || 'Unknown user'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(gen.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                {gen.user_id && (
                                  <button
                                    onClick={() => {
                                      setActiveTab('users');
                                      loadUserDetail(gen.user_id!);
                                    }}
                                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                  >
                                    <User size={12} />
                                    User
                                  </button>
                                )}
                                {gen.credits_used > 0 && (
                                  <span className="text-xs text-teal-400">{gen.credits_used} cr</span>
                                )}
                                <div className="flex items-center gap-1 ml-auto">
                                  {inputUrl && (
                                    <button
                                      onClick={() => handleDownload(inputUrl, `${gen.id}-original`)}
                                      disabled={downloadingId === `${gen.id}-original`}
                                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1 bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                      title="Download Original"
                                    >
                                      {downloadingId === `${gen.id}-original` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                      Orig
                                    </button>
                                  )}
                                  {outputUrl && (
                                    <button
                                      onClick={() => handleDownload(outputUrl, gen.id)}
                                      disabled={downloadingId === gen.id}
                                      className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 bg-teal-900/30 hover:bg-teal-900/50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                      title="Download Processed"
                                    >
                                      {downloadingId === gen.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                      Result
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination */}
                    {generationsTotal > 12 && (
                      <div className="p-4 border-t border-slate-800 flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          Showing {generationsPage * 12 + 1} - {Math.min((generationsPage + 1) * 12, generationsTotal)} of {generationsTotal}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setGenerationsPage(p => Math.max(0, p - 1));
                              setTimeout(loadGenerations, 100);
                            }}
                            disabled={generationsPage === 0}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => {
                              setGenerationsPage(p => p + 1);
                              setTimeout(loadGenerations, 100);
                            }}
                            disabled={(generationsPage + 1) * 12 >= generationsTotal}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Image size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No generations found</h4>
                    <p className="text-sm text-slate-500">Generations will appear here as users process images</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SALES & COSTS TAB */}
          {isSuperAdmin && activeTab === 'sales' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Period Filter */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">Period:</span>
                  <div className="flex gap-2">
                    {(['7d', '30d', 'all'] as SalesPeriod[]).map((period) => (
                      <button
                        key={period}
                        onClick={() => {
                          setSalesPeriod(period);
                          setTimeout(loadSalesMetrics, 100);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          salesPeriod === period
                            ? 'bg-teal-600 text-white'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        {period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : 'All Time'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {loadingSales ? (
                <div className="flex justify-center p-12">
                  <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                </div>
              ) : salesMetrics ? (
                <>
                  {/* Key Metrics Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Gross Revenue */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Gross Revenue</p>
                          <p className="text-3xl font-bold text-green-400 mt-1">
                            {formatRecordedRevenue(salesMetrics)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {recordedRevenueCaption(salesMetrics)}
                          </p>
                        </div>
                        <DollarSign size={32} className="text-green-400" />
                      </div>
                    </div>

                    {/* Total Cost */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">API Costs</p>
                          <p className="text-3xl font-bold text-red-400 mt-1">
                            ${salesMetrics.total_cost_usd.toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {salesMetrics.completed_generations} generations
                          </p>
                        </div>
                        <Target size={32} className="text-red-400" />
                      </div>
                    </div>

                    {/* Gross Margin */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Stripe amounts</p>
                          <p className="text-3xl font-bold text-white mt-1">
                            {salesMetrics.sales_with_amount ?? 0}
                            <span className="text-lg text-slate-500"> / {(salesMetrics.sales_with_amount ?? 0) + (salesMetrics.sales_without_amount ?? 0)}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Sales with the amount Stripe charged
                          </p>
                        </div>
                        <Percent size={32} className="text-slate-500" />
                      </div>
                    </div>
                  </div>

                  {/* Secondary Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Credits Used */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Credits Used</p>
                      <p className="text-2xl font-bold text-white mt-1">{salesMetrics.credits_used}</p>
                    </div>

                    {/* Credits Refunded */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Credits Refunded</p>
                      <p className="text-2xl font-bold text-yellow-400 mt-1">{salesMetrics.credits_refunded}</p>
                    </div>

                    {/* Failed Rate */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Failed Rate</p>
                      <p className={`text-2xl font-bold mt-1 ${
                        salesMetrics.failed_rate_pct <= 5 ? 'text-green-400' : 
                        salesMetrics.failed_rate_pct <= 15 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {salesMetrics.failed_rate_pct}%
                      </p>
                      <p className="text-xs text-slate-500">
                        {salesMetrics.failed_generations} / {salesMetrics.total_generations}
                      </p>
                    </div>

                    {/* Avg Cost/Gen */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Avg Cost / Gen</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {formatProviderCostUsd(salesMetrics.avg_cost_per_generation, { generations: salesMetrics.completed_generations })}
                      </p>
                    </div>
                  </div>

                  {/* Profitability Analysis */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Profitability Analysis</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Cash collected</span>
                        <span className="text-white font-medium">{formatRecordedRevenue(salesMetrics)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Avg API cost per generation</span>
                        <span className="text-white font-medium">{formatProviderCostUsd(salesMetrics.avg_cost_per_generation, { generations: salesMetrics.completed_generations })}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-slate-400">Credits granted</span>
                        <span className="text-white font-medium">{salesMetrics.credits_granted ?? salesMetrics.credits_sold}</span>
                      </div>
                    </div>

                    {(salesMetrics.sales_with_amount ?? 0) > 0 && (
                      <div className="mt-6 p-4 rounded-xl bg-black">
                        <p className="text-sm text-slate-400 mb-2">Status</p>
                        <p className="text-slate-200 font-medium">Cash is shown in the currency Stripe charged. API cost stays in US dollars, so these are not combined into one margin.</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 px-6">
                  <BarChart3 size={48} className="mx-auto text-slate-700 mb-4" />
                  <h4 className="text-slate-300 font-medium mb-1">No sales data</h4>
                  <p className="text-sm text-slate-500">Metrics will appear once purchases are made</p>
                </div>
              )}
            </div>
          )}

          {/* EDITORS TAB */}
          {isSuperAdmin && activeTab === 'editors' && (
            <div className="max-w-4xl mx-auto p-6 space-y-8">
              {loadingEditors ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-teal-400" />
                </div>
              ) : (
                <>
                  {/* All editors */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Editors</h3>

                    {/* Create editor by email (creates Auth user + adds as editor) */}
                    <div className="mb-6 pb-6 border-b border-slate-800">
                      <p className="text-sm text-slate-400 mb-3">
                        Add an editor by email. If they already have an account it is reused — they can then sign in with Google, a magic link, or an optional password. Creating again when the email exists no longer fails.
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Email</label>
                          <input
                            type="email"
                            placeholder="editor@example.com"
                            value={newEditorEmail}
                            onChange={(e) => setNewEditorEmail(e.target.value.trim())}
                            className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-64 focus:outline-none focus:border-teal-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Password (optional)</label>
                          <input
                            type="password"
                            placeholder="Leave blank to auto-generate"
                            value={newEditorPassword}
                            onChange={(e) => setNewEditorPassword(e.target.value)}
                            className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-52 focus:outline-none focus:border-teal-500"
                          />
                        </div>
                        <button
                          onClick={async () => {
                            if (!newEditorEmail) return;
                            if (newEditorPassword && newEditorPassword.length < 8) {
                              alert('Password must be at least 8 characters');
                              return;
                            }
                            setCreatingEditor(true);
                            const res = await createEditorUser(newEditorEmail, newEditorPassword || undefined);
                            setCreatingEditor(false);
                            if (res.success) {
                              setNewEditorEmail('');
                              setNewEditorPassword('');
                              loadEditorsData();
                              if (res.existing) {
                                alert('That email already has an account. They are now an editor and can sign in with Google or a magic link at sketchplan.rehub.studio.');
                              } else if (res.password) {
                                alert(`Editor created. They can sign in with Google, a magic link, or this password:\n\n${res.password}`);
                              } else {
                                alert('Editor created. They can sign in with Google or a magic link.');
                              }
                            } else {
                              alert(res.error || 'Failed to create editor');
                            }
                          }}
                          disabled={creatingEditor || !newEditorEmail}
                          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                        >
                          {creatingEditor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                          Create user & add as editor
                        </button>
                      </div>
                    </div>

                    <p className="text-sm text-slate-400 mb-4">
                      Or add an existing user by email or User ID (UUID from Auth → Users).
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <input
                        type="text"
                        placeholder="email@example.com or UUID"
                        value={newEditorUserId}
                        onChange={(e) => setNewEditorUserId(e.target.value.trim())}
                        className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-72 focus:outline-none focus:border-teal-500"
                      />
                      <button
                        onClick={async () => {
                          if (!newEditorUserId) return;
                          setAddingEditor(true);
                          const looksLikeEmail = newEditorUserId.includes('@');
                          const res = looksLikeEmail
                            ? await addEditorByEmail(newEditorUserId)
                            : await addEditor(newEditorUserId);
                          setAddingEditor(false);
                          if (res.success) {
                            setNewEditorUserId('');
                            loadEditorsData();
                          } else {
                            alert(res.error || 'Failed to add editor');
                          }
                        }}
                        disabled={addingEditor || !newEditorUserId}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                      >
                        {addingEditor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        Add existing user as editor
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {editors.length === 0 ? (
                        <li className="text-slate-500 text-sm">No editors yet.</li>
                      ) : (
                        editors.map((row) => (
                          <li key={row.user_id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-800 last:border-0">
                            <span className="text-white font-mono text-xs break-all">{row.user_id}</span>
                            <span className="text-slate-400 text-sm truncate max-w-[200px]">{row.email ?? '—'}</span>
                            <div className="flex items-center gap-3 shrink-0">
                              {row.email && (
                                <button
                                  onClick={async () => {
                                    setSendingEditorLinkFor(row.user_id);
                                    const res = await sendEditorLoginLink(row.email!);
                                    setSendingEditorLinkFor(null);
                                    if (!res.success) {
                                      alert(res.error || 'Failed to create login link');
                                      return;
                                    }
                                    if (res.action_link) {
                                      try {
                                        await navigator.clipboard.writeText(res.action_link);
                                        alert('Login link copied. Send it to them — it opens Sketchplan already signed in. They can also use Continue with Google on the login page.');
                                      } catch {
                                        alert(`Send them this login link:\n\n${res.action_link}`);
                                      }
                                    } else {
                                      alert('Login link created. Ask them to use Continue with Google or request a magic link on the login page.');
                                    }
                                  }}
                                  disabled={sendingEditorLinkFor === row.user_id}
                                  className="text-teal-400 hover:text-teal-300 text-sm font-medium disabled:opacity-50"
                                >
                                  {sendingEditorLinkFor === row.user_id ? 'Creating link…' : 'Copy login link'}
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Remove this editor? They will no longer see assigned orders.')) return;
                                  const res = await removeEditor(row.user_id);
                                  if (res.success) loadEditorsData();
                                  else alert(res.error);
                                }}
                                className="text-red-400 hover:text-red-300 text-sm font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>

                  {/* Default editors (assignment order) */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Default editors (assignment order)</h3>
                    <p className="text-sm text-slate-400 mb-4">
                      New orders are automatically assigned to the first editor in this list. Reorder to change who receives new jobs.
                    </p>
                    {defaultEditors.length === 0 ? (
                      <p className="text-slate-500 text-sm">No default editors. Add editors above, then add them to this list below.</p>
                    ) : (
                      <ol className="list-decimal list-inside space-y-2 mb-4">
                        {defaultEditors.map((row, idx) => (
                          <li key={row.user_id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                            <span className="text-white font-mono text-sm">{row.user_id}</span>
                            <span className="text-slate-400 text-sm truncate max-w-[200px] ml-2">{row.email ?? '—'}</span>
                            <button
                              onClick={async () => {
                                const next = defaultEditors.filter((r) => r.user_id !== row.user_id);
                                const res = await setDefaultEditors(next.map((r) => r.user_id));
                                if (res.success) loadEditorsData();
                                else alert(res.error);
                              }}
                              className="text-red-400 hover:text-red-300 text-sm font-medium"
                            >
                              Remove from default
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                    <div className="pt-4 border-t border-slate-800">
                      <label className="text-slate-400 text-sm block mb-2">Add editor to default list (receives new orders)</label>
                      <select
                        className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-full max-w-md focus:outline-none focus:border-teal-500"
                        value=""
                        onChange={async (e) => {
                          const uid = e.target.value;
                          if (!uid) return;
                          const next = [...defaultEditors.map((r) => r.user_id), uid];
                          const res = await setDefaultEditors(next);
                          if (res.success) loadEditorsData();
                          else alert(res.error);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Select editor to add…</option>
                        {editors
                          .filter((e) => !defaultEditors.some((d) => d.user_id === e.user_id))
                          .map((row) => (
                            <option key={row.user_id} value={row.user_id}>
                              {row.email || row.user_id}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
    </AdminShell>
  );
}
